import { FormInstance, Table, Form } from "antd";
import { useRef, useState } from "react";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetAllReservationsQuery } from "../../../../api/queries";
import { useParams } from "react-router-dom";
import { useClassEventReservationMutations } from "../../../../api/mutations";
import { IClassEventReservation } from "../../../../models/ClassEventReservation";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import { handleError } from "../../../../helper/errorHandler";
import { ColumnsType } from "antd/es/table";
import cancelIcon from "../../../../assets/images/icons/decline.svg";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import FormItemDatePicker from "../../../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../../../assets/images/icons/search-icon-white.svg";

const ClientReservations = () => {
  const { id } = useParams();
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { cancel } = useClassEventReservationMutations();

  const [filters, setFilters] = useState<{ from: Date; to: Date; searchValue: null | string }>({
    from: dayjs().subtract(7, "days").toDate(),
    to: dayjs().toDate(),
    searchValue: null,
  });
  const {
    data: reservations = [],
    isLoading,
    refetch,
  } = useGetAllReservationsQuery({ ...filters, userId: id! });

  const onFinish = (values: { from: Date; to: Date; searchValue: null | string }) => {
    setFilters({
      from: dayjs(values.from).toDate(),
      to: dayjs(values.to).toDate(),
      searchValue: values.searchValue ? values.searchValue : null,
    });
  };

  const cancelReservation = (reservation: IClassEventReservation) => {
    cancel(
      { reservationId: reservation.id, gymListingId: reservation.gymListingId },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("ReservationCanceledSuccessfully")));
          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  const columns: ColumnsType<IClassEventReservation> = [
    {
      title: t("MemberName"),
      render: (data: IClassEventReservation) => {
        return data.userFullName;
      },
    },
    {
      title: t("BookingDate"),
      render: (data: IClassEventReservation) => {
        return dayjs(data.createdAt).format("DD/MM/YYYY");
      },
    },
    {
      title: t("TimeSlot"),
      render: (data: IClassEventReservation) => {
        return `${dayjs(data.occuranceDate).format("DD/MM/YYYY hh:mm A")} ${dayjs(data.occuranceDate).add(data.duration, "minutes").format("hh:mm A")}`;
      },
    },
    {
      title: t("GymName"),
      render: (data: IClassEventReservation) => {
        return data.gymName;
      },
    },
    {
      title: t("Action"),
      render: (data: IClassEventReservation) => {
        return (
          <>
            {data.canBeCancelled ? (
              <div>
                <button
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: "Open Sans",
                    fontSize: "16px",
                  }}
                  onClick={() => cancelReservation(data)}
                >
                  <img src={cancelIcon} alt={"cancel"} />
                  {t("Cancel")}
                </button>
              </div>
            ) : (
              "-"
            )}
          </>
        );
      },
    },
  ];

  return (
    <div className={"gyms-page"}>
      <MainTitle text={t("GroupClasses")} />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="group-classes-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
          initialValues={{
            from: dayjs(filters.from),
            to: dayjs(filters.to),
          }}
        >
          <FormItemInput
            name={"searchValue"}
            label={" "}
            rules={{ required: false }}
            placeholder={t("SearchByGymName")}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"from"}
            label={t("From")}
            rules={{ required: true }}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"to"}
            label={t("To")}
            rules={{ required: true }}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={reservations}
        pagination={{ total: reservations.length }}
        loading={isLoading}
      />
    </div>
  );
};

export default ClientReservations;
